import React, { useCallback, useState, useRef, useEffect } from "react";
import { GoogleMap, Marker, Circle } from "@react-google-maps/api";
import { Input } from "reactstrap";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

const AddressAutocomplete = ({
  addressInput,
  setAddressInput,
  onPlaceChanged,
  darkMode,
  setDistanceInMeters,
}) => {
  const [predictions, setPredictions] = useState([]);
  const [autocompleteService, setAutocompleteService] = useState(null);
  const [restaurantCoords, setRestaurantCoords] = useState(null);
  const containerRef = useRef(null);
  const [showCursorTip, setShowCursorTip] = useState(false);
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const { restaurantsDetail } = useSelector((state) => state.restaurants);
  const mapCenter = restaurantCoords;

  const handleInputChange = (e) => {
    const input = e.target.value;
    setAddressInput(input);

    if (autocompleteService && restaurantCoords) {
      autocompleteService.getPlacePredictions(
        {
          input,
          location: new window.google.maps.LatLng(
            restaurantCoords.lat,
            restaurantCoords.lng
          ),
          radius: 100,
        },
        (results) => setPredictions(results || [])
      );
    }
  };

  const getDistance = (lat1, lon1, lat2, lon2) => {
    const toRad = (x) => (x * Math.PI) / 180;
    const R = 6371000;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const handlePlaceSelect = useCallback(
    (prediction) => {
      setAddressInput(prediction?.description);
      if (prediction?.place_id && restaurantCoords) {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode(
          { placeId: prediction.place_id },
          (results, status) => {
            if (status === "OK" && results[0]) {
              const placeLocation = results[0].geometry.location;
              const lat = placeLocation.lat();
              const lng = placeLocation.lng();

              const distance = getDistance(
                restaurantCoords.lat,
                restaurantCoords.lng,
                lat,
                lng
              );

              setDistanceInMeters(distance);
              if (distance > 100) {
                toast.warning(
                  `Delivery is not available at the selected address. Please choose a location within 100 meters of the restaurant located at: ${restaurantsDetail.location}`
                );
                return;
              }

              onPlaceChanged(results[0]);
            }
          }
        );
        setPredictions([]);
      }
    },
    [onPlaceChanged, restaurantCoords, setAddressInput]
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setPredictions([]);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (window.google && restaurantsDetail) {
      const { coordinate, location } = restaurantsDetail;
      if (coordinate?.latitude && coordinate?.longitude) {
        const lat = coordinate.latitude;
        const lng = coordinate.longitude;
        setRestaurantCoords({ lat, lng });

        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, (res, stat) => {
          if (stat === "OK" && res[0]) {
            setAddressInput(res[0].formatted_address);
            onPlaceChanged(res[0]);
          }
        });
      } else if (location) {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ address: location }, (results, status) => {
          if (status === "OK" && results[0]) {
            const loc = results[0].geometry.location;
            const lat = loc.lat();
            const lng = loc.lng();
            setRestaurantCoords({ lat, lng });

            const geocoder2 = new window.google.maps.Geocoder();
            geocoder2.geocode({ location: { lat, lng } }, (res, stat) => {
              if (stat === "OK" && res[0]) {
                setAddressInput(res[0].formatted_address);
                onPlaceChanged(res[0]);
              }
            });
          }
        });
      }
    }
  }, [restaurantsDetail]);

  useEffect(() => {
    if (window.google) {
      setAutocompleteService(
        new window.google.maps.places.AutocompleteService()
      );
    }
  }, []);

  return (
    <div ref={containerRef} className="mb-6 position-relative w-100 py-4">
      <Input
        type="text"
        required
        value={addressInput}
        onChange={handleInputChange}
        autoComplete="street-address"
        placeholder="Street address"
        className="w-100 px-3 py-2 rounded border"
        style={{ borderColor: "#e2e2e2" }}
      />

      {predictions?.length > 0 && (
        <div
          className="position-absolute bg-white rounded border w-100"
          style={{ borderColor: "#e2e2e2", zIndex: 10 }}
        >
          {predictions.map((prediction) => (
            <div
              key={prediction.place_id}
              onClick={() => handlePlaceSelect(prediction)}
              className="cursor-pointer p-2 text-start"
              style={{ backgroundColor: "#f8f9fa", textAlign: "left" }}
            >
              {prediction.description}
            </div>
          ))}
        </div>
      )}
      {restaurantCoords && (
        <>
          <GoogleMap
            zoom={17}
            center={mapCenter}
            mapContainerStyle={{ height: "400px", width: "100%" }}
            onLoad={(map) => (window.mapInstance = map)}
            onMouseMove={(e) => {
              setCursorPosition({
                x: e.domEvent.clientX,
                y: e.domEvent.clientY,
              });
            }}
          >
            {restaurantCoords && (
              <>
                <Marker
                  position={mapCenter}
                  draggable
                  onDragEnd={(e) => {
                    const lat = e.latLng.lat();
                    const lng = e.latLng.lng();

                    const distance = getDistance(
                      restaurantCoords.lat,
                      restaurantCoords.lng,
                      lat,
                      lng
                    );

                    setDistanceInMeters(distance);

                    if (distance > 100) {
                      toast.warning(
                        `Selected location is ${Math.round(
                          distance
                        )}m away. Must be within 100m from: ${
                          restaurantsDetail.location
                        }`
                      );
                    }

                    const geocoder = new window.google.maps.Geocoder();
                    geocoder.geocode(
                      { location: { lat, lng } },
                      (results, status) => {
                        if (status === "OK" && results[0]) {
                          setAddressInput(results[0].formatted_address);
                          onPlaceChanged(results[0]);
                        }
                      }
                    );
                  }}
                  onMouseOver={() => setShowCursorTip(true)}
                  onMouseOut={() => setShowCursorTip(false)}
                />
                <Circle
                  center={restaurantCoords}
                  radius={100}
                  options={{
                    fillColor: "#00BFFF55",
                    strokeColor: "#00BFFF",
                    strokeOpacity: 0.8,
                    strokeWeight: 1,
                    fillOpacity: 0.2,
                  }}
                />
              </>
            )}
          </GoogleMap>
          {showCursorTip && (
            <div
              style={{
                position: "fixed",
                top: cursorPosition.y + 15,
                left: cursorPosition.x + 15,
                background: "#ffffff",
                color: "#333",
                padding: "8px 12px",
                borderRadius: "8px",
                fontSize: "13px",
                fontWeight: 500,
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                pointerEvents: "none",
                zIndex: 9999,
                whiteSpace: "nowrap",
              }}
            >
              📍 Click and drag the marker to set your delivery location
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AddressAutocomplete;
